<div class="d-grid gap-3">
  <div class="row g-3">
    <div class="col-12 col-xl-4">
      <label class="form-label text-xxs fw-semibold">Row source</label>
      <select
        :value="row_source_type"
        class="form-select text-xxs rounded-small shadow-sm"
        @change="set_row_source_type($event.target.value)"
      >
        <option value="repeatable_answers">Repeatable answers</option>
        <option value="static_rows">Static rows</option>
      </select>
    </div>
    <div v-if="row_source_type === 'repeatable_answers'" class="col-12 col-xl-8">
      <label class="form-label text-xxs fw-semibold">Repeatable questions</label>
      <div class="card border rounded-small shadow-sm p-3 d-grid gap-2">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div class="text-xxs text-muted">
            {{
              row_source_question_ids.length
                ? row_source_question_ids
                    .map((question_id) => get_question_label(question_id))
                    .join(", ")
                : "No repeatable questions selected"
            }}
          </div>
          <div class="d-flex flex-wrap align-items-center gap-2">
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              type="button"
              @click="open_repeatable_question_picker"
            >
              Manage Questions
            </button>
            <span class="badge bg-light text-dark text-xxs rounded-small shadow-sm px-2 py-2">
              {{ get_repeatable_question_count() }} linked
            </span>
          </div>
        </div>
        <p class="text-muted text-xxs mb-0">
          Use Manage Questions to choose the repeatable onboarding or compliance answers that should become rows in this table.
        </p>
      </div>
    </div>
    <div v-else class="col-12">
      <div class="card border rounded-small shadow-sm p-2 formal-static-note">
        <p class="text-muted text-xxs mb-0">
          Static rows let you build a fixed table. Add the columns first, then add rows beneath them.
        </p>
      </div>
    </div>
  </div>

  <div class="d-flex justify-content-between align-items-center gap-2">
    <div>
      <label class="form-label text-xxs fw-semibold mb-1">Columns</label>
      <p class="text-muted text-xxs mb-0">
        Each column renders one bound value in the table row.
      </p>
    </div>
    <div class="d-flex flex-column align-items-end gap-1">
      <div class="d-flex flex-wrap justify-content-end align-items-center gap-2">
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          type="button"
          @click="open_bulk_column_picker"
        >
          Manage Questions
        </button>
        <span class="badge bg-light text-dark text-xxs rounded-small shadow-sm px-2 py-2">
          {{ get_column_question_count() }} linked
        </span>
        <button
          class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
          title="Add blank column"
          aria-label="Add blank column"
          type="button"
          @click="add_table_column"
        >
          <i class="fas fa-plus"></i>
        </button>
      </div>
      <p class="text-muted text-xxs mb-0 text-end">
        Choose Question in Source or use Manage Questions for onboarding or compliance fields not covered by the system-field list.
      </p>
    </div>
  </div>

  <div
    v-for="column in columns"
    :key="column.local_id"
    class="card border rounded-small shadow-sm p-3"
  >
    <div class="row g-3">
      <div class="col-12 col-xl-3">
        <label class="form-label text-xxs fw-semibold">Column label</label>
        <input
          v-model="column.label"
          class="form-control text-xxs rounded-small shadow-sm"
          :placeholder="get_label_placeholder(column)"
          type="text"
        />
      </div>
      <div class="col-12 col-xl-3">
        <label class="form-label text-xxs fw-semibold">Source</label>
        <select
          :value="column.binding.source_type"
          class="form-select text-xxs rounded-small shadow-sm"
          @change="handle_column_source_selection(column, $event.target.value)"
        >
          <option value="system_field">System field</option>
          <option value="section_question">Question</option>
          <option value="static_text">Static text</option>
        </select>
      </div>
      <div v-if="get_column_binding_source_type(column) === 'system_field'" class="col-12 col-xl-4">
        <label class="form-label text-xxs fw-semibold">System field</label>
        <select
          v-model="column.binding.system_field"
          class="form-select text-xxs rounded-small shadow-sm"
          @change="handle_label_target_change(column)"
        >
          <option v-for="option in system_field_options" :key="option.id" :value="option.id">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div v-else-if="get_column_binding_source_type(column) === 'section_question'" class="col-12 col-xl-4">
        <label class="form-label text-xxs fw-semibold">Question</label>
        <div class="card border rounded-small shadow-sm p-2 d-grid gap-2">
          <div class="text-xxs text-muted">
            {{ get_question_label(column.binding?.section_question_id) }}
          </div>
          <p class="text-muted text-xxs mb-0">
            Use Question in Source or Manage Questions above to add, remove, or reorder linked columns.
          </p>
        </div>
      </div>
      <div v-else class="col-12 col-xl-4">
        <label class="form-label text-xxs fw-semibold">Static text</label>
        <input
          v-model="column.binding.value"
          class="form-control text-xxs rounded-small shadow-sm"
          placeholder="Repeated static cell value"
          type="text"
        />
      </div>
      <div class="col-12 col-xl-2 d-flex align-items-end">
        <button
          class="btn btn-light btn-sm rounded-small text-xxs shadow-sm border text-danger"
          title="Remove column"
          aria-label="Remove column"
          type="button"
          @click="remove_table_column(column.local_id)"
        >
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>
    </div>
  </div>

    <div v-if="uses_static_rows" class="d-grid gap-3">
    <div class="d-flex justify-content-between align-items-center gap-2">
      <div>
        <label class="form-label text-xxs fw-semibold mb-1">Rows</label>
        <p class="text-muted text-xxs mb-0">
          Static rows are entered directly in the builder.
        </p>
      </div>
      <button
        class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
        type="button"
        @click="add_table_row"
      >
        Add Row
      </button>
    </div>
    <div
      v-for="(row, row_index) in rows"
      :key="`row_${row_index}`"
      class="card border rounded-small shadow-sm p-3"
    >
      <div class="d-grid gap-3 formal-binding-grid">
        <div v-for="(cell, cell_index) in row" :key="cell.local_id">
          <label class="form-label text-xxs fw-semibold">
            {{ section.config.columns?.[cell_index]?.label || `Column ${cell_index + 1}` }}
          </label>
          <input
            v-model="cell.binding.value"
            class="form-control text-xxs rounded-small shadow-sm"
            placeholder="Cell text"
            type="text"
          />
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3">
        <button
          class="btn btn-light btn-sm rounded-small text-xxs shadow-sm border text-danger"
          title="Remove row"
          aria-label="Remove row"
          type="button"
          @click="remove_table_row(row_index)"
        >
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>
    </div>
  </div>
</div>
